<script setup lang="ts">
/*
 * One line in the mini cart, as a small landscape tile: 96x54 art, two-line
 * name, line price, quantity stepper and remove (with undo). Quantity limits
 * are enforced by the cart store; the stepper only reflects them.
 */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Minus, Plus, Trash2 } from 'lucide-vue-next';
import PriceTag from '../PriceTag.vue';
import { useCartStore, type CartItem } from '../../stores/cart';
import { productArt } from '../../types/product';
import { useCartRemove } from './useCartRemove';

const props = defineProps<{ item: CartItem }>();

const { t } = useI18n();
const cart = useCartStore();
const { remove } = useCartRemove();

const art = computed(() => productArt(props.item));
const canIncrease = computed(() => props.item.qty < props.item.maxQty && !cart.isAtLimit);

const STEP_BUTTON =
  'grid size-8 place-items-center text-text-dim transition-colors hover:bg-bg-mute hover:text-text-base disabled:pointer-events-none disabled:opacity-40';

function setQty(qty: number) {
  cart.setQty(props.item.productId, qty, props.item.offerId);
}
</script>

<template>
  <li class="animate-pop flex gap-3 rounded-lg border border-border bg-bg-elev-2 p-3">
    <RouterLink :to="`/catalog/${item.productId}`" class="h-[54px] w-24 shrink-0 overflow-hidden rounded-md bg-bg-mute">
      <img v-if="art" :src="art" alt="" class="h-full w-full object-cover" />
    </RouterLink>

    <div class="flex min-w-0 flex-1 flex-col gap-2">
      <div class="flex items-start justify-between gap-2">
        <RouterLink :to="`/catalog/${item.productId}`" class="line-clamp-2 font-heading text-[12px] font-bold leading-snug text-text-base hover:text-accent">
          {{ item.name }}
        </RouterLink>
        <button
          type="button"
          class="-mr-1 -mt-1 grid size-8 shrink-0 place-items-center rounded-full text-text-faint transition-colors hover:bg-error/10 hover:text-error"
          :aria-label="t('common.miniCart.remove', { name: item.name })"
          @click="remove(item)"
        >
          <Trash2 class="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div class="flex items-center justify-between gap-2">
        <div class="inline-flex items-center overflow-hidden rounded-full border border-border bg-bg-base">
          <button type="button" :class="STEP_BUTTON" :disabled="item.qty <= 1" :aria-label="t('common.miniCart.decrease', { name: item.name })" @click="setQty(item.qty - 1)">
            <Minus class="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <span class="min-w-7 text-center text-[13px] font-semibold tabular-nums text-text-base" aria-live="polite">{{ item.qty }}</span>
          <button type="button" :class="STEP_BUTTON" :disabled="!canIncrease" :aria-label="t('common.miniCart.increase', { name: item.name })" @click="setQty(item.qty + 1)">
            <Plus class="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
        <PriceTag :cents="item.priceCents * item.qty" size="sm" />
      </div>
    </div>
  </li>
</template>
